/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import FormFader from "../Common/Fader/FormFader";
import Hidden from "@mui/material/Hidden";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import { useForm } from "../../../provider/Form/FormProvider";
import Group from "../../Wizard/Group";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import { dateFieldDefault, isIban, isNotExpiredSettlementDate, isRequired, pastOrSameDateDefault, requiredValidator } from "../../../services/validationRules";
import TerminationDueDateModal from "../../FormInfoModal/TerminationDueDateModal";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { formValue } from "../../../services/formServiceFunctions";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import ValidatorDateField from "../../FormFields/ValidatorDateField";
import { Box, Stack } from "@mui/material";
import { translate } from "../../../services/translations/translations";
import DialogModal from "../../DialogModal/DialogModal";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import InputAdornment from "@mui/material/InputAdornment";
import { IbanInput } from "../../FormFields/MaskedInputs/IbanInput";
import FormSubmitProps from "../../../types/FormSubmitProps";
import wizardStyle from "../wizardStyle";
import InfoIconButton from "../../Button/InfoIconButton";

type TerminationProps = {
  submitProps: FormSubmitProps;
};

export default function Termination({ submitProps }: TerminationProps) {
  const [infoModalOpen, setInfoModalOpen] = useState("");
  const { values, handleChange, handleSubmit } = useForm();

  const closeInfoModal = () => {
    setInfoModalOpen("");
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Kündigung
            </Typography>
          </Hidden>
          <Grid container justifyContent={"center"}>
            <Group
              type="radio"
              name="contractTerminated"
              question={translate("settlement.contractTerminated.label", values)}
              options={[
                {
                  labelText: translate("settlement.contractTerminated.options.yes.label", values),
                  labelIcon: <CheckIcon />,
                  labelIconActive: <CheckIcon active />,
                  value: "yes",
                },
                {
                  labelText: translate("settlement.contractTerminated.options.no.label", values),
                  labelIcon: <CrossIcon />,
                  labelIconActive: <CrossIcon active />,
                  value: "no",
                },
              ]}
              validators={[
                {
                  validator: isRequired,
                  message: "Bitte geben Sie an ob Sie Ihre Kündigung bereits erhalten haben",
                },
              ]}
            />
            {values.contractTerminated === "yes" && (
              <>
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <FormLegend>{translate("settlement.contractTerminationReceivedDate.question", values)}</FormLegend>
                </Grid>
                <Grid item xs={10}>
                  <ValidatorDateField
                    name="contractTerminationReceivedDate"
                    label={translate("settlement.contractTerminationReceivedDate.label", values)}
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte geben Sie das Datum an, an dem Sie die Kündigung erhalten haben",
                      },
                      dateFieldDefault,
                      pastOrSameDateDefault,
                      {
                        validator: isNotExpiredSettlementDate,
                        message: "Sie haben Ihre Kündigung vor über 3 Wochen erhalten. Ein rechtliches Vorgehen ist daher leider nicht mehr möglich.",
                      },
                    ]}
                  />
                </Grid>
                <Grid item xs={2}>
                  <Stack justifyContent={"center"} alignItems={"center"} sx={{ height: "100%" }}>
                    <InfoIconButton onClick={() => setInfoModalOpen("contractTerminationReceivedDate")} />
                  </Stack>
                  <TerminationDueDateModal open={infoModalOpen === "contractTerminationReceivedDate"} onClose={closeInfoModal} />
                </Grid>
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <FormLegend>{translate("settlement.terminationNoticeType.question", values)}</FormLegend>
                </Grid>
                <Grid item xs={10}>
                  <ValidatorSelectField
                    label={translate("settlement.terminationNoticeType.label", values)}
                    name="terminationNoticeType"
                    validators={[
                      {
                        validator: isRequired,
                        message: "Bitte geben Sie an, ob Sie fristlos oder fristgerecht gekündigt wurden.",
                      },
                    ]}
                  >
                    <MenuItem value={"withoutNotice"}>{translate("settlement.terminationNoticeType.options.withoutNotice.label", values)}</MenuItem>
                    <MenuItem value={"notice"}>{translate("settlement.terminationNoticeType.options.notice.label", values)}</MenuItem>
                    <MenuItem value={"unsure"}>{translate("settlement.terminationNoticeType.options.unsure.label", values)}</MenuItem>
                  </ValidatorSelectField>
                </Grid>
                <Grid item xs={2}>
                  <Stack justifyContent={"center"} alignItems={"center"} sx={{ height: "100%" }}>
                    <InfoIconButton onClick={() => setInfoModalOpen("terminationNoticeType")} />
                  </Stack>
                  <DialogModal
                    open={infoModalOpen === "terminationNoticeType"}
                    onClose={() => setInfoModalOpen("")}
                    title={"Der Unterschied zwischen einer fristgerechten und fristlosen Kündigung"}
                  >
                    <p>
                      Eine ordentliche Kündigung wird auch fristgerechte Kündigung genannt, da sie innerhalb der vertraglich vereinbarten Kündigungsfrist
                      ausgesprochen wird. Bei einer außerordentlichen Kündigung wird fristlos, sprich ohne Beachtung der Kündigungsfrist gekündigt. Eine
                      außerordentliche (fristlose) Kündigung ist nur rechtswirksam, wenn Sie aus wichtigem Grund ausgesprochen wird.
                    </p>
                  </DialogModal>
                </Grid>
              </>
            )}
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <FormLegend>{translate("settlement.preferredOutcome.question", values)}</FormLegend>
            </Grid>
            <Grid item xs={10}>
              <ValidatorSelectField
                label={translate("settlement.preferredOutcome.label", values)}
                name="preferredOutcome"
                validators={[{ validator: isRequired, message: "Bitte geben Sie das Ziel Ihrer Klage an." }]}
              >
                <MenuItem value={"settlement"}>{translate("settlement.preferredOutcome.options.settlement.label", values)}</MenuItem>
                <MenuItem value={"employment"}>{translate("settlement.preferredOutcome.options.employment.label", values)}</MenuItem>
              </ValidatorSelectField>
            </Grid>
            <Grid item xs={2}>
              <Stack justifyContent={"center"} alignItems={"center"} sx={{ height: "100%" }}>
                <InfoIconButton onClick={() => setInfoModalOpen("preferredOutcome")} />
              </Stack>
              <DialogModal open={infoModalOpen === "preferredOutcome"} onClose={closeInfoModal} title={"Was ist Ihr Ziel?"}>
                <p>
                  Als Arbeitnehmer haben Sie das Recht, eine Klage gegen die Kündigung einzureichen, um hiermit eine Abfindung zu erwirken oder wieder auf Ihren
                  Arbeitsplatz zurückzukehren - je nachdem, welches Ergebnis Sie sich wünschen. Wenn Sie unbedingt auf Ihrer Arbeitsstelle bleiben wollen,
                  müssen Sie anders vorgehen, als wenn Sie Ihren Job ohnehin abgeschrieben haben und nun eine möglichst hohe Abfindung erzielen wollen.
                </p>
              </DialogModal>
            </Grid>
            {values.preferredOutcome === "settlement" && (
              <>
                <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
                  <FormLegend>{translate("settlement.clientBankData.iban.question", values)}</FormLegend>
                </Grid>
                <Grid item xs={12}>
                  <ValidatorTextField
                    label={translate("settlement.clientBankData.iban.label", values)}
                    name="clientBankData.iban"
                    validators={[
                      requiredValidator("Bitte geben Sie Ihre IBAN an."),
                      {
                        validator: isIban,
                        message: "Die eingegebene IBAN ist ungültig",
                      },
                    ]}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">DE</InputAdornment>,
                      inputComponent: IbanInput,
                    }}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <FormLegend>{translate("settlement.terminationObstacles.label", values)}</FormLegend>
            </Grid>
            <Grid item xs={12}>
              <FormControl margin={"normal"} component="fieldset" required>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formValue(values, "terminationObstacles.parent")}
                        name="terminationObstacles.parent"
                        onChange={handleChange}
                        value="parent"
                      />
                    }
                    label={translate("settlement.terminationObstacles.options.parent.label", values)}
                  />
                  {values.petitioner.gender !== "male" && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!formValue(values, "terminationObstacles.pregnant")}
                          name="terminationObstacles.pregnant"
                          onChange={handleChange}
                          value="pregnant"
                        />
                      }
                      label={translate("settlement.terminationObstacles.options.pregnant.label", values)}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formValue(values, "terminationObstacles.disabled")}
                        name="terminationObstacles.disabled"
                        onChange={handleChange}
                        value="disabled"
                      />
                    }
                    label={translate("settlement.terminationObstacles.options.disabled.label", values)}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formValue(values, "terminationObstacles.council")}
                        name="terminationObstacles.council"
                        onChange={handleChange}
                        value="council"
                      />
                    }
                    label={translate("settlement.terminationObstacles.options.council.label", values)}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formValue(values, "terminationObstacles.privacyOfficer")}
                        name="terminationObstacles.privacyOfficer"
                        onChange={handleChange}
                        value="privacyOfficer"
                      />
                    }
                    label={translate("settlement.terminationObstacles.options.privacyOfficer.label", values)}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!formValue(values, "terminationObstacles.apprentice")}
                        name="terminationObstacles.apprentice"
                        onChange={handleChange}
                        value="apprentice"
                      />
                    }
                    label={translate("settlement.terminationObstacles.options.apprentice.label", values)}
                  />
                </FormGroup>
              </FormControl>
            </Grid>
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
